import { intersection, keys } from 'ramda'
import { sleep } from 'api-client/helpers'

export function getVmVolumes(vms) {
  return vms.reduce((accum, vm) => {
    const volumeNamesById = vm?.volumeIds?.reduce((accum, id, idx) => {
      return {
        ...accum,
        [id]: vm?.volumeNames?.[idx],
      }
    }, {})
    const volumes = Object.values(vm?.volumeDetails || {}) as any
    const processedVolumes = volumes.map((volume) => ({
      ...volume,
      vm: vm?.name,
      vmId: vm?.id,
      name: volumeNamesById[volume?.id],
    }))
    return [...accum, ...processedVolumes]
  }, [])
}

export function getVmPorts(vms) {
  return vms.reduce((accum, vm) => {
    const networkKeys = keys(vm?.addresses)
    const allAddresses = networkKeys.reduce((_accum, networkKey) => {
      const networkAddresses = vm?.addresses?.[networkKey]
      const networkAddressesWithNetworkName = networkAddresses.map((address) => ({
        ...address,
        networkName: networkKey,
      }))
      return [..._accum, ...networkAddressesWithNetworkName]
    }, [])
    const mappedPorts =
      vm?.ports?.map((port) => {
        const matchingAddress = allAddresses.find(
          (address) => address?.['OS-EXT-IPS-MAC:mac_addr'] === port?.mac_address,
        )
        return {
          ...port,
          networkName: matchingAddress?.networkName,
          ipAddress: matchingAddress?.addr,
          ipVersion: matchingAddress?.version,
          ipType: matchingAddress?.['OS-EXT-IPS:type'],
          vm: vm?.name,
          vmId: vm?.id,
        }
      }) || []
    return [...accum, ...mappedPorts]
  }, [])
}

export async function deleteSnapshotsAndWait({
  vmVolumes,
  volumeSnapshots,
  additionalVolumeIds,
  setHaveVolumeSnapshotsToDelete,
  bulkRemoveSnapshots,
  getVolumeSnapshots,
}) {
  const deleteOnTerminationVolumes = vmVolumes.filter((volume) => volume?.delete_on_termination)
  const volumeIds = deleteOnTerminationVolumes.map((volume) => volume.id)
  const volumeSnapshotsToDelete = volumeSnapshots.filter((snapshot) =>
    [...volumeIds, ...additionalVolumeIds].includes(snapshot.volume_id),
  )
  if (volumeSnapshotsToDelete.length === 0) {
    return
  }
  setHaveVolumeSnapshotsToDelete(true)
  const deleteObjects = volumeSnapshotsToDelete.map((snapshot) => ({
    id: snapshot?.id,
  }))
  const snapshotResults = await bulkRemoveSnapshots(deleteObjects)
  const successfulDeletions = snapshotResults.filter((result) => result.success)
  const successfulIds = successfulDeletions.map((result) => result.params?.id)
  console.log(successfulIds, 'successfulIds')

  let allSnapshotsDeleted = false
  console.log(allSnapshotsDeleted, 'allSnapshotsDeleted')
  while (!allSnapshotsDeleted) {
    await sleep(5000)
    const snapshotsList = await getVolumeSnapshots()
    const allSnapshotIds = snapshotsList.map((snapshot) => snapshot.id)
    allSnapshotsDeleted = intersection(successfulIds, allSnapshotIds)?.length === 0
  }
}

export async function initiateSimpleVmDeletion({
  deleteDependentSnapshots,
  virtualMachines,
  bulkRemove,
  setVmDeletionResults,
}) {
  await deleteDependentSnapshots()
  const rowsWithBody = virtualMachines.map((vm) => ({
    id: vm?.id,
    name: vm?.name,
  }))
  const results = await bulkRemove(rowsWithBody)
  const mappedResults = results.map((result) => ({
    success: result?.success,
    vmId: result?.params?.id,
    // @ts-ignore
    vmName: result?.params?.name,
  }))
  setVmDeletionResults(mappedResults)
}

export async function initiateResourcesDeletion({
  virtualMachines,
  vmVolumes,
  vmPorts,
  deleteDependentSnapshots,
  bulkRemove,
  bulkRemovePorts,
  enableTerminationForVolumes,
  setVmDeletionResults,
  setVolumeDeletionResults,
  setPortDeletionResults,
}) {
  const manualDeleteVolumes = vmVolumes.filter((volume) => !volume.delete_on_termination)
  const deleteOnTerminationVolumes = vmVolumes.filter((volume) => volume.delete_on_termination)
  const terminationVolumes = manualDeleteVolumes.map((volume) => ({
    volumeId: volume?.id,
    vmId: volume?.vmId,
  }))
  const { succeeded, failed } = await enableTerminationForVolumes(terminationVolumes)

  await deleteDependentSnapshots(succeeded)
  const rowsWithBody = virtualMachines.map((vm) => ({
    id: vm?.id,
    name: vm?.name,
  }))
  const vmResults = await bulkRemove(rowsWithBody)
  const mappedVmResults = vmResults.map((result) => ({
    success: result?.success,
    vmId: result?.params?.id,
    // @ts-ignore
    vmName: result?.params?.name,
  }))
  setVmDeletionResults(mappedVmResults)

  const manualVolumeResults = manualDeleteVolumes.map((volume) => ({
    success:
      succeeded.includes(volume.id) &&
      !!mappedVmResults.find((vm) => vm.vmId === volume.vmId)?.success,
    volumeId: volume.id,
    volumeName: volume.name,
  }))
  const deleteOnTerminationVolumeResults = deleteOnTerminationVolumes.map((volume) => {
    return {
      success: !!mappedVmResults.find((vm) => vm.vmId === volume.vmId)?.success,
      volumeId: volume.id,
      volumeName: volume.name,
    }
  })
  setVolumeDeletionResults([...manualVolumeResults, ...deleteOnTerminationVolumeResults])

  const manualDeletePorts = vmPorts.filter((port) => !port.delete_on_termination)
  const deleteOnTerminationPorts = vmPorts.filter((port) => port.delete_on_termination)
  const portsWithBody = manualDeletePorts.map((port) => ({
    id: port?.id,
    // @ts-ignore
    ipAddress: port?.ipAddress,
  }))
  const portResults = await bulkRemovePorts(portsWithBody)
  const mappedPortResults = portResults.map((result) => ({
    success: result?.success,
    portId: result?.params?.id,
    // @ts-ignore
    portAddress: result?.params?.ipAddress,
  }))
  const deleteOnTerminationPortResults = deleteOnTerminationPorts.map((port) => {
    return {
      success: !!mappedVmResults.find((vm) => vm.vmId === port.vmId)?.success,
      portId: port.id,
      portAddress: port.ipAddress,
    }
  })
  setPortDeletionResults([...mappedPortResults, ...deleteOnTerminationPortResults])
}
